/**
 * <vfw-glossary> — filter + category chips for Shop Talk.
 *
 * Progressive enhancement only. Every term, every definition and every letter
 * group is in the HTML before this file runs; JS narrows what is shown and
 * nothing else. With JS off the page is a complete, readable A–Z.
 *
 * Matching is deliberately generous: term + aliases + the definition text, so
 * "back spacing", "PCD" and "hub bore" all land on the right card.
 */
class VfwGlossary extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-gloss-search]');
    this.chips = Array.from(this.querySelectorAll('[data-gloss-chip]'));
    this.cards = Array.from(this.querySelectorAll('[data-gloss-card]'));
    this.groups = Array.from(this.querySelectorAll('[data-gloss-group]'));
    this.azLinks = Array.from(this.querySelectorAll('[data-gloss-az]'));
    this.indexItems = Array.from(this.querySelectorAll('[data-gloss-index-item]'));
    this.indexGroups = Array.from(this.querySelectorAll('[data-gloss-index-group]'));
    this.empty = this.querySelector('[data-gloss-empty]');
    this.status = this.querySelector('[data-gloss-status]');

    this.category = 'all';
    this.query = '';

    if (this.input) {
      this.input.addEventListener('input', () => {
        this.query = this.input.value.trim().toLowerCase();
        this.apply();
      });
      // A stray Enter inside a lone field submits in some browsers.
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
        if (e.key === 'Escape') { this.input.value = ''; this.query = ''; this.apply(); }
      });
    }

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const next = chip.dataset.glossChip;
        this.category = this.category === next ? 'all' : next;
        this.chips.forEach((c) =>
          c.setAttribute('aria-pressed', String(c.dataset.glossChip === this.category))
        );
        this.apply();
      });
    });

    this.apply();
  }

  apply() {
    const q = this.query;
    const cat = this.category;
    let shown = 0;

    this.cards.forEach((card) => {
      const inCat = cat === 'all' || card.dataset.category === cat;
      const hit = !q || (card.dataset.haystack || '').includes(q);
      const on = inCat && hit;
      card.hidden = !on;
      if (on) shown += 1;
    });

    // A letter heading with nothing under it is noise — hide the whole group.
    const liveLetters = new Set();
    this.groups.forEach((group) => {
      const any = Array.from(group.querySelectorAll('[data-gloss-card]')).some((c) => !c.hidden);
      group.hidden = !any;
      if (any) liveLetters.add(group.dataset.letter);
    });

    // Index rail mirrors the same state.
    this.indexItems.forEach((item) => {
      const card = item.dataset.for ? document.getElementById(item.dataset.for) : null;
      item.hidden = !card || card.hidden;
    });
    this.indexGroups.forEach((group) => {
      const any = Array.from(group.querySelectorAll('[data-gloss-index-item]')).some((i) => !i.hidden);
      group.hidden = !any;
    });

    // Letters stay in place and grey out — never reflow the alphabet.
    this.azLinks.forEach((link) => {
      const live = liveLetters.has(link.dataset.glossAz);
      link.setAttribute('aria-disabled', String(!live));
      link.tabIndex = live ? 0 : -1;
    });

    if (this.empty) this.empty.hidden = shown !== 0;
    if (this.status) {
      this.status.textContent =
        shown === 0
          ? 'No terms match.'
          : `${shown} term${shown === 1 ? '' : 's'} shown.`;
    }
  }
}

if (!customElements.get('vfw-glossary')) {
  customElements.define('vfw-glossary', VfwGlossary);
}
